import { useMemo, useState } from 'react';
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ChartCard, EmptyState } from './chart-card';
import { TrendChart } from './charts';
import { condense, ReportModel, SeriesPoint } from './report-data';
import { inr, palette } from './report-theme';
import { neutral } from '../../theme/colors';

type Granularity = 'daily' | 'weekly' | 'monthly';

const seriesFor = (report: ReportModel, granularity: Granularity): SeriesPoint[] => {
	if (granularity === 'weekly') return report.weeklySales;
	if (granularity === 'monthly') return report.monthlySales;
	return report.dailySales;
};

const labelFor: Record<Granularity, string> = { daily: 'Day', weekly: 'Week', monthly: 'Month' };

interface TrendPanelProps {
	report: ReportModel;
	/** Granularities offered in the toggle. One option hides the toggle. */
	options?: Granularity[];
	initial?: Granularity;
	height?: number;
	title?: string;
}

export const TrendPanel = ({
	report,
	options = ['daily', 'weekly', 'monthly'],
	initial,
	height = 320,
	title = 'Revenue trend',
}: TrendPanelProps) => {
	const available = options.filter((option) => seriesFor(report, option).length > 0);
	const [granularity, setGranularity] = useState<Granularity>(
		initial ?? available.find((option) => seriesFor(report, option).length > 1) ?? available[0] ?? 'daily'
	);

	const points = useMemo(() => condense(seriesFor(report, granularity)), [report, granularity]);
	const peak = useMemo(
		() => points.reduce((best: SeriesPoint | null, point) => (!best || point.value > best.value ? point : best), null),
		[points]
	);

	const toggle = available.length > 1 && (
		<ToggleButtonGroup
			exclusive
			size="small"
			value={granularity}
			onChange={(_event, value) => value && setGranularity(value)}
			sx={{
				'& .MuiToggleButton-root': {
					px: 1.5,
					py: 0.5,
					textTransform: 'none',
					fontSize: 13,
					borderColor: neutral[200],
					color: 'text.secondary',
					'&.Mui-selected': { backgroundColor: palette.single, color: '#fff', '&:hover': { backgroundColor: palette.single } },
				},
			}}
		>
			{available.map((option) => (
				<ToggleButton key={option} value={option}>
					{labelFor[option]}
				</ToggleButton>
			))}
		</ToggleButtonGroup>
	);

	const subtitle = points.length > 1 && peak
		? `Best ${labelFor[granularity].toLowerCase()}: ${peak.label} at ${inr(peak.value)}`
		: 'Revenue booked in the selected range';

	return (
		<ChartCard title={title} subtitle={subtitle} action={toggle || undefined}>
			{points.length > 1 && (
				<TrendChart
					categories={points.map((point) => point.label)}
					series={[{ name: 'Revenue', data: points.map((point) => point.value) }]}
					height={height}
				/>
			)}
			{/* One point is a number, not a chart. Show it as one. */}
			{points.length === 1 && (
				<Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ minHeight: 200, py: 4 }}>
					<Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
						{points[0].label}
					</Typography>
					<Typography variant="h3" sx={{ fontWeight: 600 }}>
						{inr(points[0].value)}
					</Typography>
					<Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, maxWidth: 420 }}>
						{`Only one ${labelFor[granularity].toLowerCase()} in this range. Pick a wider range above to see how revenue moves.`}
					</Typography>
				</Stack>
			)}
			{points.length === 0 && <EmptyState height={200} message="No sales recorded in the selected range." />}
		</ChartCard>
	);
};
